import {
  AcceptUserAgreementsResult,
  AcceptUserAgreementsResultTypenames,
  AgreementType,
  ApplicationProductTypeEnum,
} from '@varo/graphql-schema-types';
import PlaidLogo from 'assets/images/vxl/logos/plaid/plaid-logo.svg';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  PLAID_LINK_ORIGINS,
  PlaidLinkOrigin,
} from 'features/plaid/constants/PlaidLinkOrigins';
import { PlaidMarketingConsentEntryPoint } from 'features/plaid/hooks/useHasPlaidMarketingConsent';
import useLogPlaidConsentError from 'features/plaid/hooks/useLogPlaidConsentError';
import { useOpenUrlDocumentScreen } from 'features/urlDocumentScreens/hooks/useOpenUrlDocumentScreen';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { requestContentCardsRefresh } from 'services/engagement/braze/braze';
import AcceptUserAgreements from 'services/graphql/mutations/MoveMoney/AcceptUserAgreements';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import VaroLogo from 'shared/components/vxl/VaroLogo/VaroLogo';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import useAgreements from 'shared/hooks/useAgreements/useAgreements';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import useEvent from 'shared/hooks/useEvent/useEvent';
import { PlaidIntent, PlaidIntentTypes } from 'shared/hooks/usePlaidV2/types';
import usePlaidV2 from 'shared/hooks/usePlaidV2/usePlaidV2';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import theme from 'shared/styles/theme';

const MarketingConsentScreen = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLaunchingPlaid, setIsLaunchingPlaid] = useState(false);

  const shouldNavigateBackAfterRefreshRef = useRef(false);
  const hasStartedHomeTileRefreshRef = useRef(false);

  const router = useRouter();
  const { entryPoint } = useLocalSearchParams<{
    entryPoint?: PlaidMarketingConsentEntryPoint;
  }>();

  const { bottom } = useSafeAreaInsets();

  let homeTileId: string | undefined;

  if (entryPoint === PlaidMarketingConsentEntryPoint.Home) {
    homeTileId = HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU;
  } else if (entryPoint === PlaidMarketingConsentEntryPoint.LinkedAccounts) {
    homeTileId = HomeTileTypeIds.LINKED_ACCOUNTS_TILE;
  }

  const homeTileIds = homeTileId ? [homeTileId] : undefined;
  // A route param can carry an origin this screen has no mapping for.
  const plaidOrigin: PlaidLinkOrigin | undefined = entryPoint
    ? PLAID_LINK_ORIGINS[entryPoint]
    : undefined;
  const { openPdfDocumentScreen } = useOpenUrlDocumentScreen();
  const {
    agreementsListDetails,
    loading: isLoadingAgreements,
    refresh: refreshAgreements,
  } = useAgreements([ApplicationProductTypeEnum.PlaidMarketingConsent]);
  const [{ fetching: isAcceptingAgreement }, acceptUserAgreements] =
    useCancellableMutation<{
      acceptUserAgreements: AcceptUserAgreementsResult;
    }>(AcceptUserAgreements);
  const { refreshTiles, fetching: isRefreshingHomeTiles } =
    useRefreshHomeTiles(homeTileIds);
  const plaidIntent: PlaidIntentTypes =
    isLaunchingPlaid && plaidOrigin
      ? {
          intent: PlaidIntent.Link,
          product: plaidOrigin.product,
          entryPoint: plaidOrigin.entryPoint,
        }
      : { intent: PlaidIntent.Wait };
  const {
    openPlaid,
    ready: isPlaidReady,
    error: plaidError,
    retry: retryPlaidToken,
    fetching: isPreparingPlaid,
  } = usePlaidV2(plaidIntent, { sourceName: plaidOrigin?.sourceName });
  const { t: translatedText } = useTranslation('plaidStrings', {
    keyPrefix: 'marketingConsent',
  });
  const logConsentError = useLogPlaidConsentError(plaidOrigin);
  const handleBackAnalytics = usePressableAnalytics({
    analyticsId: 'plaidConsentBackArrow',
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: '/plaid/marketing-consent',
  });

  const plaidMarketingConsentAgreementId = agreementsListDetails?.find(
    (agreement) => agreement.type === AgreementType.PlaidMarketingConsent,
  )?.id;
  const retryPlaidLaunch =
    isLaunchingPlaid && plaidError ? retryPlaidToken : undefined;
  const isSubmittingConsent = isAcceptingAgreement || isLaunchingPlaid;

  const getDisplayedErrorMessage = () => {
    if (errorMessage) {
      return errorMessage;
    }

    if (!plaidOrigin) {
      return translatedText('unknownOriginError');
    }

    if (retryPlaidLaunch) {
      return translatedText('plaidLaunchError');
    }

    return null;
  };

  const displayedErrorMessage = getDisplayedErrorMessage();

  const handleBack = () => {
    handleBackAnalytics();
    router.back();
  };

  const handleOpenAgreement = (agreementType: AgreementType) => {
    const agreementUrl = agreementsListDetails?.find(
      (agreement) => agreement.type === agreementType,
    )?.resource?.pdfDocument?.url;

    if (!agreementUrl) {
      return;
    }

    openPdfDocumentScreen(agreementUrl);
  };

  const handleAgreeAndContinue = async () => {
    if (!plaidMarketingConsentAgreementId) {
      logConsentError({
        errorStep: 'agreement_load',
        errorType: 'validation',
        errorCode: 'MissingMarketingConsentAgreement',
      });
      setErrorMessage(translatedText('loadingError'));
      return;
    }

    try {
      const response = await acceptUserAgreements({
        input: { agreementIds: [plaidMarketingConsentAgreementId] },
      });
      const acceptedAgreement = response.data?.acceptUserAgreements;
      const isAgreementAccepted =
        acceptedAgreement?.__typename ===
          AcceptUserAgreementsResultTypenames.AcceptUserAgreementsSuccess &&
        acceptedAgreement.accepted;

      if (response.error || !isAgreementAccepted) {
        logConsentError({
          errorStep: 'agreement_accept',
          errorType: response.error ? 'system' : 'validation',
          errorCode: response.error?.name ?? acceptedAgreement?.__typename,
          errorMessage: response.error?.message,
        });
        setErrorMessage(translatedText('submitError'));
        return;
      }

      setErrorMessage(null);
      setIsLaunchingPlaid(true);
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
      logConsentError({
        errorStep: 'agreement_accept',
        errorType: 'system',
        errorCode: error instanceof Error ? error.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : undefined,
      });
      setErrorMessage(translatedText('submitError'));
    }
  };

  const handleRetry = () => {
    if (retryPlaidLaunch) {
      void retryPlaidLaunch();
      return;
    }

    if (!plaidMarketingConsentAgreementId) {
      setErrorMessage(null);
      refreshAgreements();
      return;
    }

    void handleAgreeAndContinue();
  };

  const handlePlaidLaunch = useEvent(async () => {
    const result = await openPlaid();

    setIsLaunchingPlaid(false);

    if (result.status === 'blocked') {
      logConsentError({
        errorStep: 'plaid_open',
        errorType: 'system',
        errorCode: 'PlaidOpenBlocked',
        errorMessage: result.reason,
      });
    }

    if (result.status !== 'success') {
      router.back();
      return;
    }

    if (entryPoint === PlaidMarketingConsentEntryPoint.Home) {
      requestContentCardsRefresh();
    }

    if (!homeTileId) {
      router.back();
      return;
    }

    shouldNavigateBackAfterRefreshRef.current = true;
    refreshTiles();
  });

  useEffect(() => {
    if (plaidOrigin) {
      return;
    }

    logConsentError({
      errorStep: 'consent_entry_point',
      errorType: 'validation',
      errorCode: 'UnknownPlaidOrigin',
      errorMessage: entryPoint,
    });
  }, [entryPoint, logConsentError, plaidOrigin]);

  useEffect(() => {
    if (!isLaunchingPlaid || !isPlaidReady) {
      return;
    }

    void handlePlaidLaunch();
  }, [handlePlaidLaunch, isLaunchingPlaid, isPlaidReady]);

  useEffect(() => {
    if (!isLaunchingPlaid || !plaidError || retryPlaidToken) {
      return;
    }

    router.back();
  }, [isLaunchingPlaid, plaidError, retryPlaidToken, router]);

  useEffect(() => {
    if (!shouldNavigateBackAfterRefreshRef.current) {
      return;
    }

    if (isRefreshingHomeTiles) {
      hasStartedHomeTileRefreshRef.current = true;
      return;
    }

    if (!hasStartedHomeTileRefreshRef.current) {
      return;
    }

    shouldNavigateBackAfterRefreshRef.current = false;
    hasStartedHomeTileRefreshRef.current = false;
    router.back();
  }, [isRefreshingHomeTiles, router]);

  const errorPrimaryButton = plaidOrigin
    ? {
        title: translatedText('retryCta'),
        onPress: handleRetry,
        isLoading: isAcceptingAgreement || isPreparingPlaid,
      }
    : {
        title: translatedText('goBackCta'),
        onPress: () => router.back(),
      };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={handleBack} />
          ),
        }}
      />
      {displayedErrorMessage ? (
        <ErrorScreen
          screenType={ErrorScreenType.Error}
          messageHeadline={translatedText('errorHeadline')}
          messageBody={displayedErrorMessage}
          primaryButton={errorPrimaryButton}
        />
      ) : (
        <>
          <ScrollView
            style={styles.contentScrollView}
            contentContainerStyle={styles.contentContainer}
          >
            <View style={styles.content}>
              <View
                style={styles.illustration}
                accessible
                accessibilityRole="image"
                accessibilityLabel="Varo connected to Plaid"
              >
                <View style={styles.varoLogoTile}>
                  <VaroLogo color="white" width={31} height={12} />
                </View>
                <View style={styles.connectorDots}>
                  <View style={styles.connectorDot} />
                  <View style={styles.connectorDot} />
                  <View style={styles.connectorDot} />
                </View>
                <View style={styles.plaidLogoTile}>
                  <PlaidLogo width={25} height={26} />
                </View>
              </View>
              <View style={styles.messaging}>
                <TitleLgText style={styles.headline} numberOfLines={0}>
                  {translatedText('headline')}
                </TitleLgText>
                <BodyBaseText style={styles.subhead} numberOfLines={0}>
                  {translatedText('subhead')}
                </BodyBaseText>
              </View>
              <View style={styles.benefitList}>
                <View style={styles.benefitRow}>
                  <Icon
                    iconKey="LOCK"
                    iconSize="MD"
                    iconColor={theme.color.text.tertiary}
                  />
                  <BodyBaseText style={styles.benefitText} numberOfLines={0}>
                    {translatedText('encryptedAndSecure')}
                  </BodyBaseText>
                </View>
                <View style={styles.benefitRow}>
                  <Icon
                    iconKey="BOLT"
                    iconSize="MD"
                    iconColor={theme.color.text.tertiary}
                  />
                  <BodyBaseText style={styles.benefitText} numberOfLines={0}>
                    {translatedText('fastAndEasySetup')}
                  </BodyBaseText>
                </View>
              </View>
            </View>
          </ScrollView>
          <View
            style={[
              styles.footer,
              {
                paddingBottom: bottom + theme.spacing.m,
              },
            ]}
          >
            <BodyXSText style={styles.legalCopy} numberOfLines={0}>
              {translatedText('legalPrefix')}
              <HyperlinkXsText
                onPress={() =>
                  handleOpenAgreement(AgreementType.GeneralTermsAgreement)
                }
                accessibilityLabel={translatedText('generalTerms')}
              >
                {translatedText('generalTerms')}
              </HyperlinkXsText>
              {translatedText('legalMiddle')}
              <HyperlinkXsText
                onPress={() =>
                  handleOpenAgreement(
                    AgreementType.OnlineAndMobilePrivacyPolicy,
                  )
                }
                accessibilityLabel={translatedText('privacyPolicy')}
              >
                {translatedText('privacyPolicy')}
              </HyperlinkXsText>
              {translatedText('legalSuffix')}
            </BodyXSText>
            <Button
              label={translatedText('agreeCta')}
              analyticsId="plaidConsentAgreeAndContinue"
              isDisabled={isLoadingAgreements || isSubmittingConsent}
              isLoading={isSubmittingConsent}
              onPress={handleAgreeAndContinue}
            />
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  benefitList: {
    alignItems: 'center',
    gap: theme.spacing.s,
    paddingTop: theme.spacing.xl,
  },
  benefitRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.s,
  },
  benefitText: {
    color: theme.color.text.secondary,
    textAlign: 'center',
  },
  connectorDot: {
    backgroundColor: theme.color.text.tertiary,
    borderRadius: 2,
    height: 4,
    width: 4,
  },
  connectorDots: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  content: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.l,
  },
  contentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingBottom: theme.spacing.xl,
    paddingTop: theme.spacing.xl,
  },
  contentScrollView: {
    flex: 1,
  },
  footer: {
    gap: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  headline: {
    textAlign: 'center',
  },
  illustration: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.s,
    justifyContent: 'center',
    marginBottom: theme.spacing.xl,
  },
  legalCopy: {
    color: theme.color.text.secondary,
  },
  messaging: {
    gap: theme.spacing.s,
    maxWidth: 327,
  },
  plaidLogoTile: {
    alignItems: 'center',
    borderColor: theme.color.border.primary,
    borderRadius: 6,
    borderWidth: 1,
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  subhead: {
    textAlign: 'center',
  },
  varoLogoTile: {
    alignItems: 'center',
    backgroundColor: theme.color.expressive.varoPurple,
    borderRadius: 6,
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
});

export default MarketingConsentScreen;
